/**
 * GraphicActionOverlay surfaces the actions for a selected tactical line:
 * redraw its shape, or delete it. Same pattern, chrome and dismiss-on-leave as
 * the marker overlay — clicking an entity offers its actions rather than doing
 * something irreversible straight away.
 *
 * Anchored at the **midpoint of the drawn centreline** rather than at the click
 * point: the row has to keep a fixed map position to stay pinned through pans,
 * and the midpoint is the one point that is predictable for any line, including
 * one selected by clicking near its tail.
 */

import React from "react";
import { faTrash, faPen } from "@fortawesome/free-solid-svg-icons";
import MapActionOverlay from "./MapActionOverlay";
import type { TacticalGraphic } from "../../store/graphicsSlice";

export interface GraphicActionOverlayProps {
    graphic: TacticalGraphic | null;
    /** Redraw the line's geometry, keeping its identity and attachment. */
    onEdit: (graphicId: string) => void;
    onDelete: (graphicId: string) => void;
    onDismiss: () => void;
}

/**
 * Midpoint of a line's centreline, as `{lat, lng}`.
 *
 * The middle *vertex* for an odd count, otherwise the halfway point of the
 * middle segment — so the row lands on the line itself rather than off in space
 * for a bent arrow, which averaging every vertex would do.
 */
export function centrelineMidpoint(
    points: number[][],
): { lat: number; lng: number } | null {
    if (!points || points.length === 0) return null;
    if (points.length % 2 === 1) {
        const [lng, lat] = points[(points.length - 1) / 2];
        return { lat, lng };
    }
    const [aLng, aLat] = points[points.length / 2 - 1];
    const [bLng, bLat] = points[points.length / 2];
    return { lat: (aLat + bLat) / 2, lng: (aLng + bLng) / 2 };
}

export default function GraphicActionOverlay({
    graphic,
    onEdit,
    onDelete,
    onDismiss,
}: GraphicActionOverlayProps) {
    const anchor = graphic ? centrelineMidpoint(graphic.points) : null;

    return (
        <MapActionOverlay
            testId="graphic-action-overlay"
            anchor={anchor}
            onDismiss={onDismiss}
            actions={
                graphic
                    ? [
                          {
                              icon: faPen,
                              label: "Redraw line",
                              testId: "graphic-action-edit",
                              onClick: () => onEdit(graphic.id),
                          },
                          {
                              icon: faTrash,
                              label: "Delete line",
                              testId: "graphic-action-delete",
                              danger: true,
                              onClick: () => onDelete(graphic.id),
                          },
                      ]
                    : []
            }
        />
    );
}
